import { clsx } from 'clsx';
import { useMemo, type ReactNode } from 'react';
import { Btn, Card, CardHeaderInverse, Select } from '../ui';
import { ITEM_STATUS_CODES, INPUT_MODES, LOCATION_TYPES } from '../../utils/constants';
import { getOperatorResponseChoices, requiresNotesOrMedia, OPERATOR_RESPONSE_VALUES } from '../../utils/formPromptMapping';
import {
    DAILY_SERVICE_NEW_ISSUES_CHOICES,
    isDailyServiceNewIssuesRecording,
} from '../../utils/dailyServiceNewIssues';
import { displayUnitOfMeasure } from '../../utils/unitOfMeasureUtils';
import type { UnitOfMeasureOption } from '../../utils/unitOfMeasureUtils';
import type { tGPSInput, tInspectionFormInput, tMediaInput } from '../../types';
import type { SingleUnit } from '../../types/schema';
import { UMCounterLowReadingWarning } from '../UMCounterLowReadingWarning';
import {
    getWizardProgressPercent,
    getWizardSectionMeta,
    isFourResponseStep,
    isUmCounterStep,
    isYesNoStep,
    isCheckboxStep,
    isSectionQuestionStep,
    isDropdownStep,
    isOperatorResponseStep,
    isQuantityOnlyStep,
    isAcknowledgementStep,
    isNewIssuesStep,
    isStandAloneFieldStep,
    createRadioGroupKeyDownHandler,
} from './inspectionWizardUtils';
import { InspectFlowResponseIcon } from './InspectFlowResponseIcon';
import { InspectFlowWarnInopDetails, type InspectFlowWarnInopDetailsProps } from './InspectFlowWarnInopDetails';
import '../../css/InspectionFlowWizard.css';

type InspectFlowWizardProps = {
    equipmentTitle: string;
    formSubtitle: string;
    locationLabel: string;
    prompts: tInspectionFormInput[];
    activeStepIndex: number;
    userLocation: tGPSInput | null;
    selectedUnit: SingleUnit;
    umCatalog: UnitOfMeasureOption[];
    isLoading: boolean;
    canGoNext: boolean;
    nextStepBlockedReason?: string | null;
    stepMediaItems?: tMediaInput[];
    audioBlob?: Blob;
    recorderStatus?: string;
    onExit: () => void;
    onStartOver?: () => void;
    showStartOver?: boolean;
    onPrevious: () => void;
    onNext: () => void;
    onPromptValueChange: (index: number, value: string) => void;
    onPromptNotesChange: (index: number, notes: string) => void;
    onPromptCheckboxToggle?: (index: number, option: string) => void;
    onFilesSelected?: (files: FileList) => void;
    onStartRecording?: () => void;
    onStopRecording?: () => void;
    onRemoveAudio?: () => void;
    onRemoveMediaItem?: (item: tMediaInput) => void;
    legacyPromptContent?: ReactNode;
    flowKind?: 'inspection' | 'dailyService';
};

const RESPONSE_OPTIONS = [
    {
        value: ITEM_STATUS_CODES.ok,
        title: 'OK',
        description: 'Good condition, no issues',
        tone: 'ok' as const,
    },
    {
        value: ITEM_STATUS_CODES.warn,
        title: 'Warning',
        description: 'Needs attention, still operable',
        tone: 'warn' as const,
    },
    {
        value: ITEM_STATUS_CODES.inop,
        title: 'Inoperative',
        description: 'Failed — equipment should not operate',
        tone: 'inop' as const,
    },
    {
        value: ITEM_STATUS_CODES.na,
        title: 'Not Applicable',
        description: 'Does not apply to this equipment',
        tone: 'na' as const,
    },
];

const YES_NO_OPTIONS = [
    {
        value: ITEM_STATUS_CODES.ok,
        title: 'Yes',
        description: 'Operational',
        tone: 'ok' as const,
    },
    {
        value: ITEM_STATUS_CODES.warn,
        title: 'No',
        description: 'Issue Found',
        tone: 'inop' as const,
    },
];

type FlowResponseOption = (typeof RESPONSE_OPTIONS)[number] | (typeof YES_NO_OPTIONS)[number];

const formatResponseOptionTitle = (option: FlowResponseOption): string =>
    option.description ? `${option.title}, ${option.description}` : option.title;

const formatLocationStatus = (userLocation: tGPSInput | null): string => {
    if (!userLocation?.type) {
        return 'Detecting location…';
    }

    if (userLocation.type === LOCATION_TYPES.gps) {
        return 'GPS auto-detected';
    }

    if (userLocation.type === LOCATION_TYPES.lastKnownPosition) {
        return 'Last known position';
    }

    return 'Location unavailable';
};

const resolveOperatorChoiceTone = (value: string): 'ok' | 'warn' | 'inop' | 'na' => {
    switch (value) {
        case ITEM_STATUS_CODES.ok:
        case OPERATOR_RESPONSE_VALUES.added:
            return 'ok';
        case ITEM_STATUS_CODES.na:
            return 'na';
        case ITEM_STATUS_CODES.inop:
            return 'inop';
        case ITEM_STATUS_CODES.warn:
        case OPERATOR_RESPONSE_VALUES.notAdded:
        case OPERATOR_RESPONSE_VALUES.cleaned:
        case OPERATOR_RESPONSE_VALUES.replaced:
            return 'warn';
        case OPERATOR_RESPONSE_VALUES.noNewIssues:
            return 'ok';
        default:
            return 'ok';
    }
};

const renderInspectingBlock = (
    prompt: tInspectionFormInput,
    { hideDuplicateTitle = true }: { hideDuplicateTitle?: boolean } = {},
) => (
    <div className="inspect-flow-wizard-inspecting">
        <p className="inspect-flow-wizard-inspecting-label">What you are inspecting</p>
        <p
            className="inspect-flow-wizard-inspecting-value"
            {...(hideDuplicateTitle ? { 'aria-hidden': true } : {})}
        >
            {prompt.label}
        </p>
    </div>
);

const renderPartMeta = (prompt: tInspectionFormInput) => {
    const parts = [
        prompt.partNumber ? `#${prompt.partNumber}` : null,
        prompt.partDescription?.trim() || prompt.vendor?.trim() || null,
        prompt.defaultUM ? `UM: ${prompt.defaultUM}` : null,
    ].filter(Boolean);

    if (!parts.length) {
        return null;
    }

    return <p className="inspect-flow-wizard-part-meta">{parts.join(' · ')}</p>;
};

const optionRequiresWarnInopDetails = (value: string) =>
    value === ITEM_STATUS_CODES.warn || value === ITEM_STATUS_CODES.inop;

const ResponseOptionWrap = ({
    tone,
    isSelected,
    showEmbeddedDetails,
    children,
}: {
    tone: string;
    isSelected: boolean;
    showEmbeddedDetails: boolean;
    children: ReactNode;
}) => (
    <div
        className={clsx(
            'inspect-flow-response-option-wrap',
            `tone-${tone}`,
            isSelected && 'is-selected',
            showEmbeddedDetails && 'has-warn-inop-details',
        )}
    >
        {children}
    </div>
);

type WizardStepNavProps = {
    primaryButtonLabel: string;
    disableNext: boolean;
    nextBlockedReason?: string | null;
    onNext?: () => void;
    onPrevious?: () => void;
    showPrevious?: boolean;
    buttonType?: 'button' | 'submit';
};

const WizardStepNav = ({
    primaryButtonLabel,
    disableNext,
    nextBlockedReason = null,
    onNext,
    onPrevious,
    showPrevious = false,
    buttonType = 'button',
}: WizardStepNavProps) => {
    const hintId = 'inspect-flow-next-blocked-hint';
    const showHint = Boolean(nextBlockedReason) && disableNext;

    return (
        <div className="inspect-flow-wizard-card-footer">
            {showHint && (
                <p id={hintId} className="inspect-flow-wizard-next-hint" role="status" aria-live="polite">
                    {nextBlockedReason}
                </p>
            )}
            <div className="inspect-flow-wizard-nav-buttons">
                {showPrevious && onPrevious && (
                    <Btn
                        variant="secondary-soft-outlined"
                        buttonText="← Previous"
                        buttonOnClick={onPrevious}
                    />
                )}
                <Btn
                    variant="primary"
                    buttonType={buttonType}
                    buttonText={`${primaryButtonLabel} →`}
                    buttonDisabled={disableNext}
                    buttonOnClick={buttonType === 'submit' ? undefined : onNext}
                    buttonAriaProps={showHint ? { 'aria-describedby': hintId } : undefined}
                />
            </div>
        </div>
    );
};

export const InspectFlowWizard = ({
    equipmentTitle,
    formSubtitle,
    locationLabel,
    prompts,
    activeStepIndex,
    userLocation,
    selectedUnit,
    umCatalog,
    isLoading,
    canGoNext,
    nextStepBlockedReason = null,
    onExit,
    onStartOver,
    showStartOver = false,
    onPrevious,
    onNext,
    onPromptValueChange,
    onPromptNotesChange,
    onPromptCheckboxToggle,
    stepMediaItems = [],
    audioBlob,
    recorderStatus = 'idle',
    onFilesSelected,
    onStartRecording,
    onStopRecording,
    onRemoveAudio,
    onRemoveMediaItem,
    legacyPromptContent,
    flowKind = 'inspection',
}: InspectFlowWizardProps) => {
    const totalSteps = prompts.length;
    const currentPrompt = prompts[activeStepIndex];
    const progressPercent = getWizardProgressPercent(activeStepIndex, totalSteps);
    const sectionMeta = getWizardSectionMeta(prompts, activeStepIndex);
    const umLabel = displayUnitOfMeasure(selectedUnit.unitOfMeasure, umCatalog) || 'hrs';
    const isBeforeBegin = isUmCounterStep(currentPrompt);
    const isFourResponse = isFourResponseStep(currentPrompt);
    const isYesNo = isYesNoStep(currentPrompt);
    const isCheckbox = isCheckboxStep(currentPrompt);
    const isSectionQuestion = isSectionQuestionStep(currentPrompt);
    const isDropdown = isDropdownStep(currentPrompt);
    const isOperatorResponse = isOperatorResponseStep(currentPrompt);
    const isQuantityOnly = isQuantityOnlyStep(currentPrompt);
    const isAcknowledgement = isAcknowledgementStep(currentPrompt);
    const isNewIssues = isNewIssuesStep(currentPrompt);
    const isStandAloneField = isStandAloneFieldStep(currentPrompt);
    const operatorChoices = isOperatorResponse ? getOperatorResponseChoices(currentPrompt?.subType) : null;
    const flowSectionLabel = flowKind === 'dailyService' ? 'Daily Service' : 'Inspection';
    const exitLabel = flowKind === 'dailyService' ? 'Exit Daily Service' : 'Exit Inspection';
    const showWarnInopDetails =
        Boolean(currentPrompt?.value) &&
        !isNewIssues &&
        (requiresNotesOrMedia(currentPrompt) ||
            ((isFourResponse || isYesNo) &&
                (currentPrompt?.value === ITEM_STATUS_CODES.warn ||
                    (isFourResponse && currentPrompt?.value === ITEM_STATUS_CODES.inop))));
    const warnInopTone =
        currentPrompt?.value === ITEM_STATUS_CODES.inop ? ('inop' as const) : ('warn' as const);
    const hasActiveDiscrepancy =
        Boolean(currentPrompt?.hasActiveDiscrepancy) &&
        currentPrompt?.discrepancyStatus !== ITEM_STATUS_CODES.ok;

    const hideNaOption =
        isFourResponse &&
        currentPrompt?.hasActiveDiscrepancy &&
        currentPrompt.discrepancyStatus === ITEM_STATUS_CODES.warn;

    const visibleResponseOptions = hideNaOption
        ? RESPONSE_OPTIONS.filter((option) => option.value !== ITEM_STATUS_CODES.na)
        : RESPONSE_OPTIONS;

    const handleFourResponseRadioKeyDown = useMemo(
        () =>
            createRadioGroupKeyDownHandler(visibleResponseOptions, (value) =>
                onPromptValueChange(activeStepIndex, value),
            ),
        [activeStepIndex, onPromptValueChange, visibleResponseOptions],
    );

    const handleYesNoRadioKeyDown = useMemo(
        () =>
            createRadioGroupKeyDownHandler(YES_NO_OPTIONS, (value) =>
                onPromptValueChange(activeStepIndex, value),
            ),
        [activeStepIndex, onPromptValueChange],
    );

    const handleOperatorRadioKeyDown = useMemo(() => {
        if (!operatorChoices) {
            return undefined;
        }

        return createRadioGroupKeyDownHandler(operatorChoices, (value) =>
            onPromptValueChange(activeStepIndex, value),
        );
    }, [activeStepIndex, onPromptValueChange, operatorChoices]);

    const handleNewIssuesRadioKeyDown = useMemo(
        () =>
            createRadioGroupKeyDownHandler(DAILY_SERVICE_NEW_ISSUES_CHOICES, (value) =>
                onPromptValueChange(activeStepIndex, value),
            ),
        [activeStepIndex, onPromptValueChange],
    );

    const primaryButtonLabel = isBeforeBegin
        ? flowKind === 'dailyService'
            ? 'Start Daily Service'
            : 'Start Inspection'
        : activeStepIndex >= totalSteps - 1
          ? 'Review and Sign'
          : 'Next';

    // Block advancing while the step can't proceed OR page data is still loading.
    const disableNext = !canGoNext || isLoading;

    const stepTitleId = `inspect-flow-step-title-${activeStepIndex}`;
    const stepQuestionId = `inspect-flow-step-question-${activeStepIndex}`;
    const stepCardEyebrow = `Section ${sectionMeta?.sectionIndex ?? 1} · ${sectionMeta?.sectionTitle ?? flowSectionLabel}`;
    const stepCardBadge = `Item ${sectionMeta?.itemIndex ?? 1} of ${sectionMeta?.itemCount ?? 1}`;
    const warnInopDetailsProps: Omit<InspectFlowWarnInopDetailsProps, 'tone' | 'placement'> = {
        notes: currentPrompt?.notes || '',
        onNotesChange: (notes) => onPromptNotesChange(activeStepIndex, notes),
        mediaItems: stepMediaItems,
        audioBlob,
        recorderStatus,
        onFilesSelected: onFilesSelected ?? (() => undefined),
        onStartRecording: onStartRecording ?? (() => undefined),
        onStopRecording: onStopRecording ?? (() => undefined),
        onRemoveAudio: onRemoveAudio ?? (() => undefined),
        onRemoveMediaItem: onRemoveMediaItem ?? (() => undefined),
    };
    const renderWarnInopDetails = (placement: 'embedded' | 'standalone', tone: 'warn' | 'inop') => (
        <InspectFlowWarnInopDetails {...warnInopDetailsProps} tone={tone} placement={placement} />
    );
    const renderNewIssuesDetails = (placement: 'embedded' | 'standalone') => (
        <InspectFlowWarnInopDetails
            {...warnInopDetailsProps}
            id={`inspect-flow-new-issues-details-${activeStepIndex}-${placement}`}
            tone="warn"
            placement={placement}
            heading="Record the issue"
            notesVisibleLabel="Notes"
            onFilesSelected={(files) => {
                onFilesSelected?.(files);
                onPromptValueChange(activeStepIndex, ITEM_STATUS_CODES.warn);
            }}
            onStopRecording={() => {
                onStopRecording?.();
                onPromptValueChange(activeStepIndex, ITEM_STATUS_CODES.warn);
            }}
        />
    );

    return (
        <div className="inspect-flow-wizard">
            <Card variant="card-lg" className="inspect-flow-wizard-header-card card-pad-lg">
                <header className="inspect-flow-wizard-top">
                    <div className="inspect-flow-wizard-top-text">
                        <h1 className="inspect-flow-wizard-title">{equipmentTitle}</h1>
                        <p className="inspect-flow-wizard-subtitle">{formSubtitle}</p>
                    </div>
                    <div className="inspect-flow-wizard-top-actions">
                        <span className="inspect-flow-wizard-progress-pill">{progressPercent}% Complete</span>
                        {showStartOver && onStartOver && (
                            <Btn
                                variant="tertiary-outlined"
                                buttonText="Start over"
                                buttonOnClick={onStartOver}
                                buttonClasses="inspect-flow-wizard-start-over-btn"
                            />
                        )}
                        <Btn
                            variant="secondary-soft-outlined"
                            buttonText={exitLabel}
                            buttonOnClick={onExit}
                            buttonClasses="inspect-flow-wizard-exit-btn"
                        />
                    </div>
                </header>

                <div className="inspect-flow-wizard-progress-row">
                    <div className="inspect-flow-wizard-progress-labels">
                        {sectionMeta && !isBeforeBegin && (
                            <div className="inspect-flow-wizard-section-title-wrap">
                                <span className="inspect-flow-wizard-section-title">{sectionMeta.sectionTitle}</span>
                                <span className="inspect-flow-wizard-section-badge">
                                    Section {sectionMeta.sectionIndex} of {sectionMeta.sectionCount}
                                </span>
                            </div>
                        )}
                        <span className="inspect-flow-wizard-progress-step-label">
                            Step {activeStepIndex + 1} of {totalSteps}
                        </span>
                    </div>
                    <div
                        className="inspect-flow-wizard-progress-track"
                        role="progressbar"
                        aria-valuenow={progressPercent}
                        aria-valuemin={0}
                        aria-valuemax={100}
                        aria-label={`Inspection progress: ${progressPercent}% complete, step ${activeStepIndex + 1} of ${totalSteps}`}
                    >
                        <div
                            className="inspect-flow-wizard-progress-fill"
                            style={{ width: `${Math.max(progressPercent, 2)}%` }}
                        />
                    </div>
                </div>
            </Card>

            {isBeforeBegin && currentPrompt && (
                <Card variant="card-lg" className="inspect-flow-wizard-card">
                    <CardHeaderInverse
                        title="Before You Begin"
                        subtitle={`Enter the current ${umLabel} to log this ${flowKind === 'dailyService' ? 'daily service' : 'inspection'}`}
                    />
                    <form
                        className="inspect-flow-wizard-before-begin-form"
                        onSubmit={(event) => {
                            event.preventDefault();
                            if (!disableNext) {
                                onNext();
                            }
                        }}
                    >
                        <div className="inspect-flow-wizard-card-body">
                            <label className="inspect-flow-wizard-field-label" htmlFor="inspect-flow-um-input">
                                Current {umLabel}
                            </label>
                            <p className="inspect-flow-wizard-field-hint">
                                Enter the current {umLabel} reading from the instrument panel
                            </p>
                            <input
                                id="inspect-flow-um-input"
                                className="inspect-flow-wizard-um-input"
                                type="number"
                                inputMode="decimal"
                                enterKeyHint="go"
                                value={currentPrompt.value}
                                onChange={(event) => onPromptValueChange(activeStepIndex, event.target.value)}
                                onKeyDown={(event) => {
                                    if (event.key !== 'Enter') {
                                        return;
                                    }

                                    event.preventDefault();
                                    if (!disableNext) {
                                        onNext();
                                    }
                                }}
                                autoFocus
                            />
                            <p className="inspect-flow-wizard-um-unit">{umLabel || 'hrs'}</p>
                            <UMCounterLowReadingWarning
                                enteredValue={currentPrompt.value}
                                unit={selectedUnit}
                                unitOfMeasure={selectedUnit.unitOfMeasure}
                            />
                            <div className="inspect-flow-wizard-location-bar">
                                <span className="inspect-flow-wizard-location-pin" aria-hidden="true">
                                    📍
                                </span>
                                <span>
                                    Location: <strong>{locationLabel}</strong>
                                </span>
                                <span className="inspect-flow-wizard-location-status">
                                    {formatLocationStatus(userLocation)}
                                </span>
                            </div>
                        </div>
                        <WizardStepNav
                            primaryButtonLabel={primaryButtonLabel}
                            disableNext={disableNext}
                            nextBlockedReason={nextStepBlockedReason}
                            buttonType="submit"
                        />
                    </form>
                </Card>
            )}

            {isFourResponse && currentPrompt && (
                <Card
                    variant="card-lg"
                    className={clsx(
                        'inspect-flow-wizard-card',
                        hasActiveDiscrepancy && 'inspect-flow-wizard-card--active-discrepancy',
                    )}
                >
                    <CardHeaderInverse
                        eyebrow={stepCardEyebrow}
                        badge={stepCardBadge}
                        title={currentPrompt.label}
                        titleId={stepTitleId}
                        titleSize="item"
                    />
                    <div className="inspect-flow-wizard-card-body">
                        {renderInspectingBlock(currentPrompt)}
                        {currentPrompt.description && (
                            <div className="inspect-flow-wizard-what-to-check" id={stepQuestionId}>
                                <strong>What to check:</strong> {currentPrompt.description}
                            </div>
                        )}
                        {currentPrompt.hasActiveDiscrepancy && currentPrompt.discrepancyNotes && (
                            <div className="inspect-flow-wizard-previous-notes">
                                <strong>Previous report notes:</strong> {currentPrompt.discrepancyNotes}
                            </div>
                        )}
                        <div
                            className="inspect-flow-wizard-response-grid"
                            role="radiogroup"
                            aria-labelledby={stepTitleId}
                            aria-describedby={currentPrompt.description ? stepQuestionId : undefined}
                            onKeyDown={handleFourResponseRadioKeyDown}
                        >
                            {visibleResponseOptions.map((option) => {
                                const isWarnStatusUnchanged =
                                    option.value === ITEM_STATUS_CODES.warn &&
                                    currentPrompt.hasActiveDiscrepancy &&
                                    currentPrompt.discrepancyStatus === ITEM_STATUS_CODES.warn;
                                const isNaStatusUnchanged =
                                    option.value === ITEM_STATUS_CODES.na &&
                                    currentPrompt.hasActiveDiscrepancy &&
                                    currentPrompt.discrepancyStatus !== ITEM_STATUS_CODES.warn;
                                const isSelected =
                                    currentPrompt.value === option.value ||
                                    Boolean(
                                        (isWarnStatusUnchanged || isNaStatusUnchanged) &&
                                            currentPrompt.value === ITEM_STATUS_CODES.status_unchanged,
                                    );
                                const optionLabel =
                                    isWarnStatusUnchanged || isNaStatusUnchanged
                                        ? 'Status Unchanged'
                                        : `${option.title}, ${option.description}`;
                                const showEmbeddedDetails =
                                    isSelected &&
                                    showWarnInopDetails &&
                                    optionRequiresWarnInopDetails(option.value);
                                const embeddedDetailsTone =
                                    option.value === ITEM_STATUS_CODES.inop ? ('inop' as const) : ('warn' as const);

                                return (
                                    <ResponseOptionWrap
                                        key={option.value}
                                        tone={option.tone}
                                        isSelected={isSelected}
                                        showEmbeddedDetails={showEmbeddedDetails}
                                    >
                                        <button
                                            type="button"
                                            role="radio"
                                            aria-checked={isSelected}
                                            aria-label={optionLabel}
                                            className={clsx(
                                                'inspect-flow-response-option',
                                                `tone-${option.tone}`,
                                                isSelected && 'is-selected',
                                            )}
                                            onClick={() => {
                                                if (isNaStatusUnchanged || isWarnStatusUnchanged) {
                                                    onPromptValueChange(
                                                        activeStepIndex,
                                                        ITEM_STATUS_CODES.status_unchanged,
                                                    );
                                                    return;
                                                }

                                                onPromptValueChange(activeStepIndex, option.value);
                                            }}
                                        >
                                            <span className="inspect-flow-response-heading">
                                                <span className="inspect-flow-response-icon" aria-hidden="true">
                                                    <InspectFlowResponseIcon tone={option.tone} />
                                                </span>
                                                <span className="inspect-flow-response-title">
                                                    {isWarnStatusUnchanged || isNaStatusUnchanged
                                                        ? 'Status Unchanged'
                                                        : option.title}
                                                </span>
                                            </span>
                                            {!isWarnStatusUnchanged && !isNaStatusUnchanged && (
                                                <span className="inspect-flow-response-description">
                                                    {option.description}
                                                </span>
                                            )}
                                        </button>
                                        {showEmbeddedDetails &&
                                            renderWarnInopDetails('embedded', embeddedDetailsTone)}
                                    </ResponseOptionWrap>
                                );
                            })}
                        </div>
                        {showWarnInopDetails && renderWarnInopDetails('standalone', warnInopTone)}
                    </div>
                    <WizardStepNav
                        primaryButtonLabel={primaryButtonLabel}
                        disableNext={disableNext}
                        nextBlockedReason={nextStepBlockedReason}
                        onNext={onNext}
                        onPrevious={onPrevious}
                        showPrevious={activeStepIndex > 0}
                    />
                </Card>
            )}

            {isYesNo && currentPrompt && (
                <Card variant="card-lg" className="inspect-flow-wizard-card">
                    <CardHeaderInverse
                        eyebrow={stepCardEyebrow}
                        badge={stepCardBadge}
                        title={currentPrompt.label}
                        titleId={stepTitleId}
                        titleSize="item"
                    />
                    <div className="inspect-flow-wizard-card-body">
                        {renderInspectingBlock(currentPrompt)}
                        {currentPrompt.description && (
                            <div className="inspect-flow-wizard-what-to-check" id={stepQuestionId}>
                                <strong>What to check:</strong> {currentPrompt.description}
                            </div>
                        )}
                        {currentPrompt.hasActiveDiscrepancy && currentPrompt.discrepancyNotes && (
                            <div className="inspect-flow-wizard-previous-notes">
                                <strong>Previous report notes:</strong> {currentPrompt.discrepancyNotes}
                            </div>
                        )}
                        <div
                            className="inspect-flow-wizard-response-grid inspect-flow-wizard-response-grid--binary"
                            role="radiogroup"
                            aria-labelledby={stepTitleId}
                            aria-describedby={currentPrompt.description ? stepQuestionId : undefined}
                            onKeyDown={handleYesNoRadioKeyDown}
                        >
                            {YES_NO_OPTIONS.map((option) => {
                                const isNoStatusUnchanged =
                                    option.value === ITEM_STATUS_CODES.warn &&
                                    currentPrompt.hasActiveDiscrepancy &&
                                    currentPrompt.discrepancyStatus === ITEM_STATUS_CODES.warn;
                                const isSelected =
                                    currentPrompt.value === option.value ||
                                    Boolean(
                                        isNoStatusUnchanged &&
                                            currentPrompt.value === ITEM_STATUS_CODES.status_unchanged,
                                    );
                                const cardTone =
                                    option.value === ITEM_STATUS_CODES.ok
                                        ? 'ok'
                                        : isSelected
                                          ? 'warn'
                                          : 'na';
                                const iconTone =
                                    option.value === ITEM_STATUS_CODES.ok ? ('ok' as const) : ('inop' as const);
                                const optionLabel = isNoStatusUnchanged
                                    ? 'Status Unchanged'
                                    : formatResponseOptionTitle(option);
                                const showEmbeddedDetails =
                                    isSelected &&
                                    showWarnInopDetails &&
                                    optionRequiresWarnInopDetails(option.value);

                                return (
                                    <ResponseOptionWrap
                                        key={option.value}
                                        tone={cardTone}
                                        isSelected={isSelected}
                                        showEmbeddedDetails={showEmbeddedDetails}
                                    >
                                        <button
                                            type="button"
                                            role="radio"
                                            aria-checked={isSelected}
                                            aria-label={optionLabel}
                                            className={clsx(
                                                'inspect-flow-response-option',
                                                'inspect-flow-response-option--row',
                                                `tone-${cardTone}`,
                                                isSelected && 'is-selected',
                                            )}
                                            onClick={() => {
                                                if (isNoStatusUnchanged) {
                                                    onPromptValueChange(
                                                        activeStepIndex,
                                                        ITEM_STATUS_CODES.status_unchanged,
                                                    );
                                                    return;
                                                }

                                                onPromptValueChange(activeStepIndex, option.value);
                                            }}
                                        >
                                            <span className="inspect-flow-response-heading">
                                                <span className="inspect-flow-response-icon" aria-hidden="true">
                                                    <InspectFlowResponseIcon tone={iconTone} />
                                                </span>
                                                <span className="inspect-flow-response-title">
                                                    {isNoStatusUnchanged
                                                        ? 'Status Unchanged'
                                                        : formatResponseOptionTitle(option)}
                                                </span>
                                            </span>
                                        </button>
                                        {showEmbeddedDetails && renderWarnInopDetails('embedded', 'warn')}
                                    </ResponseOptionWrap>
                                );
                            })}
                        </div>
                        {showWarnInopDetails && renderWarnInopDetails('standalone', 'warn')}
                    </div>
                    <WizardStepNav
                        primaryButtonLabel={primaryButtonLabel}
                        disableNext={disableNext}
                        nextBlockedReason={nextStepBlockedReason}
                        onNext={onNext}
                        onPrevious={onPrevious}
                        showPrevious={activeStepIndex > 0}
                    />
                </Card>
            )}

            {isCheckbox && currentPrompt && (
                <Card variant="card-lg" className="inspect-flow-wizard-card">
                    <CardHeaderInverse
                        eyebrow={stepCardEyebrow}
                        badge={stepCardBadge}
                        title={currentPrompt.label}
                        titleId={stepTitleId}
                        titleSize="item"
                    />
                    <div className="inspect-flow-wizard-card-body">
                        {currentPrompt.description && (
                            <div className="inspect-flow-wizard-what-to-check inspect-flow-wizard-checkbox-question" id={stepQuestionId}>
                                {currentPrompt.description}
                            </div>
                        )}
                        {!currentPrompt.description && renderInspectingBlock(currentPrompt, { hideDuplicateTitle: false })}
                        <div
                            className="inspect-flow-checkbox-options"
                            role="group"
                            aria-labelledby={stepTitleId}
                            aria-describedby={currentPrompt.description ? stepQuestionId : undefined}
                        >
                            {(currentPrompt.options ?? [])
                                .filter(
                                    (option: string) =>
                                        typeof option === 'string' && option.trim().length > 0,
                                )
                                .map((option: string) => {
                                    const isSelected =
                                        currentPrompt.selectedOptions?.includes(option) ?? false;

                                    return (
                                        <button
                                            key={option}
                                            type="button"
                                            role="checkbox"
                                            aria-checked={isSelected}
                                            className={clsx(
                                                'inspect-flow-checkbox-option',
                                                isSelected && 'is-selected',
                                            )}
                                            onClick={() =>
                                                onPromptCheckboxToggle?.(activeStepIndex, option)
                                            }
                                        >
                                            <span className="inspect-flow-checkbox-box" aria-hidden="true">
                                                {isSelected && (
                                                    <svg
                                                        className="inspect-flow-checkbox-check"
                                                        viewBox="0 0 12 10"
                                                        fill="none"
                                                    >
                                                        <path
                                                            d="M1 5.5L4.5 9L11 1.5"
                                                            stroke="currentColor"
                                                            strokeWidth="2"
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                        />
                                                    </svg>
                                                )}
                                            </span>
                                            <span className="inspect-flow-checkbox-label">{option}</span>
                                        </button>
                                    );
                                })}
                        </div>
                    </div>
                    <WizardStepNav
                        primaryButtonLabel={primaryButtonLabel}
                        disableNext={disableNext}
                        nextBlockedReason={nextStepBlockedReason}
                        onNext={onNext}
                        onPrevious={onPrevious}
                        showPrevious={activeStepIndex > 0}
                    />
                </Card>
            )}

            {isDropdown && currentPrompt && (
                <Card variant="card-lg" className="inspect-flow-wizard-card inspect-flow-wizard-card--dropdown">
                    <CardHeaderInverse
                        eyebrow={stepCardEyebrow}
                        badge={stepCardBadge}
                        title={currentPrompt.label}
                        titleId={stepTitleId}
                        titleSize="item"
                    />
                    <div className="inspect-flow-wizard-card-body">
                        {currentPrompt.description && (
                            <div className="inspect-flow-wizard-what-to-check inspect-flow-wizard-checkbox-question" id={stepQuestionId}>
                                {currentPrompt.description}
                            </div>
                        )}
                        {!currentPrompt.description && renderInspectingBlock(currentPrompt, { hideDuplicateTitle: false })}
                        <Select
                            className="inspect-flow-wizard-dropdown-select"
                            value={currentPrompt.value || ''}
                            onChange={(value) => onPromptValueChange(activeStepIndex, value)}
                            placeholder="Select an option"
                            ariaLabel={currentPrompt.label || 'Select an option'}
                            ariaDescribedBy={currentPrompt.description ? stepQuestionId : undefined}
                            options={(currentPrompt.options ?? [])
                                .filter(
                                    (option: string) =>
                                        typeof option === 'string' && option.trim().length > 0,
                                )
                                .map((option: string) => ({
                                    label: option,
                                    value: option,
                                }))}
                        />
                    </div>
                    <WizardStepNav
                        primaryButtonLabel={primaryButtonLabel}
                        disableNext={disableNext}
                        nextBlockedReason={nextStepBlockedReason}
                        onNext={onNext}
                        onPrevious={onPrevious}
                        showPrevious={activeStepIndex > 0}
                    />
                </Card>
            )}

            {isSectionQuestion && currentPrompt && (
                <Card variant="card-lg" className="inspect-flow-wizard-card">
                    <CardHeaderInverse
                        eyebrow={stepCardEyebrow}
                        badge={stepCardBadge}
                        title={currentPrompt.label}
                        titleId={stepTitleId}
                        titleSize="item"
                    />
                    <div className="inspect-flow-wizard-card-body">
                        {currentPrompt.description && (
                            <>
                                <p className="inspect-flow-wizard-inspecting-label">What you are looking for</p>
                                <div
                                    className="inspect-flow-wizard-what-to-check inspect-flow-wizard-checkbox-question"
                                    id={stepQuestionId}
                                >
                                    {currentPrompt.description}
                                </div>
                            </>
                        )}
                        <label className="visually-hidden" htmlFor={`inspect-flow-question-${activeStepIndex}`}>
                            {currentPrompt.label || currentPrompt.description || 'Your answer'}
                        </label>
                        {currentPrompt.inputMode === INPUT_MODES.TEXTAREA ? (
                            <textarea
                                id={`inspect-flow-question-${activeStepIndex}`}
                                className="inspect-flow-wizard-question-textarea"
                                value={currentPrompt.value}
                                onChange={(event) =>
                                    onPromptValueChange(activeStepIndex, event.target.value)
                                }
                                aria-describedby={currentPrompt.description ? stepQuestionId : undefined}
                                autoFocus
                                rows={4}
                            />
                        ) : (
                            <input
                                id={`inspect-flow-question-${activeStepIndex}`}
                                className="inspect-flow-wizard-question-input"
                                type={
                                    currentPrompt.inputMode === INPUT_MODES.NUMBER ? 'number' : 'text'
                                }
                                inputMode={
                                    currentPrompt.inputMode === INPUT_MODES.NUMBER ? 'decimal' : 'text'
                                }
                                value={currentPrompt.value}
                                onChange={(event) =>
                                    onPromptValueChange(activeStepIndex, event.target.value)
                                }
                                aria-describedby={currentPrompt.description ? stepQuestionId : undefined}
                                autoFocus
                            />
                        )}
                    </div>
                    <WizardStepNav
                        primaryButtonLabel={primaryButtonLabel}
                        disableNext={disableNext}
                        nextBlockedReason={nextStepBlockedReason}
                        onNext={onNext}
                        onPrevious={onPrevious}
                        showPrevious={activeStepIndex > 0}
                    />
                </Card>
            )}

            {isOperatorResponse && currentPrompt && operatorChoices && (
                <Card
                    variant="card-lg"
                    className={clsx(
                        'inspect-flow-wizard-card',
                        hasActiveDiscrepancy && 'inspect-flow-wizard-card--active-discrepancy',
                    )}
                >
                    <CardHeaderInverse
                        eyebrow={stepCardEyebrow}
                        badge={stepCardBadge}
                        title={currentPrompt.label}
                        titleId={stepTitleId}
                        titleSize="item"
                    />
                    <div className="inspect-flow-wizard-card-body">
                        {renderPartMeta(currentPrompt)}
                        {currentPrompt.description && (
                            <div className="inspect-flow-wizard-what-to-check" id={stepQuestionId}>
                                <strong>What to check:</strong> {currentPrompt.description}
                            </div>
                        )}
                        {currentPrompt.hasActiveDiscrepancy && currentPrompt.discrepancyNotes && (
                            <div className="inspect-flow-wizard-previous-notes">
                                <strong>Previous report notes:</strong> {currentPrompt.discrepancyNotes}
                            </div>
                        )}
                        <div
                            className="inspect-flow-wizard-response-grid inspect-flow-wizard-response-grid--compact"
                            role="radiogroup"
                            aria-labelledby={stepTitleId}
                            onKeyDown={handleOperatorRadioKeyDown}
                        >
                            {operatorChoices.map((choice) => {
                                const tone = resolveOperatorChoiceTone(choice.value);
                                const isSelected = currentPrompt.value === choice.value;
                                const showEmbeddedDetails =
                                    isSelected &&
                                    showWarnInopDetails &&
                                    (tone === 'warn' || tone === 'inop');
                                const embeddedDetailsTone = tone === 'inop' ? ('inop' as const) : ('warn' as const);

                                return (
                                    <ResponseOptionWrap
                                        key={choice.value}
                                        tone={tone}
                                        isSelected={isSelected}
                                        showEmbeddedDetails={showEmbeddedDetails}
                                    >
                                        <button
                                            type="button"
                                            role="radio"
                                            aria-checked={isSelected}
                                            aria-label={choice.label}
                                            className={clsx(
                                                'inspect-flow-response-option',
                                                `tone-${tone}`,
                                                isSelected && 'is-selected',
                                            )}
                                            onClick={() => onPromptValueChange(activeStepIndex, choice.value)}
                                        >
                                            <span className="inspect-flow-response-heading">
                                                <span className="inspect-flow-response-icon" aria-hidden="true">
                                                    <InspectFlowResponseIcon tone={tone} />
                                                </span>
                                                <span className="inspect-flow-response-title">{choice.label}</span>
                                            </span>
                                        </button>
                                        {showEmbeddedDetails &&
                                            renderWarnInopDetails('embedded', embeddedDetailsTone)}
                                    </ResponseOptionWrap>
                                );
                            })}
                        </div>
                        {showWarnInopDetails && renderWarnInopDetails('standalone', warnInopTone)}
                    </div>
                    <WizardStepNav
                        primaryButtonLabel={primaryButtonLabel}
                        disableNext={disableNext}
                        nextBlockedReason={nextStepBlockedReason}
                        onNext={onNext}
                        onPrevious={onPrevious}
                        showPrevious={activeStepIndex > 0}
                    />
                </Card>
            )}

            {isQuantityOnly && currentPrompt && (
                <Card variant="card-lg" className="inspect-flow-wizard-card">
                    <CardHeaderInverse
                        eyebrow={stepCardEyebrow}
                        badge={stepCardBadge}
                        title={currentPrompt.label}
                        titleId={stepTitleId}
                        titleSize="item"
                    />
                    <div className="inspect-flow-wizard-card-body">
                        {renderPartMeta(currentPrompt)}
                        {currentPrompt.description && (
                            <div className="inspect-flow-wizard-what-to-check" id={stepQuestionId}>
                                <strong>What to check:</strong> {currentPrompt.description}
                            </div>
                        )}
                        <label className="inspect-flow-wizard-field-label" htmlFor={`inspect-flow-quantity-${activeStepIndex}`}>
                            {currentPrompt.defaultUM ? `Quantity (${currentPrompt.defaultUM})` : 'Quantity'}
                        </label>
                        <input
                            id={`inspect-flow-quantity-${activeStepIndex}`}
                            className="inspect-flow-wizard-question-input"
                            type="number"
                            inputMode="decimal"
                            min={0}
                            step="any"
                            value={currentPrompt.value}
                            onChange={(event) => onPromptValueChange(activeStepIndex, event.target.value)}
                            autoFocus
                        />
                    </div>
                    <WizardStepNav
                        primaryButtonLabel={primaryButtonLabel}
                        disableNext={disableNext}
                        nextBlockedReason={nextStepBlockedReason}
                        onNext={onNext}
                        onPrevious={onPrevious}
                        showPrevious={activeStepIndex > 0}
                    />
                </Card>
            )}

            {isNewIssues && currentPrompt && (
                <Card variant="card-lg" className="inspect-flow-wizard-card">
                    <CardHeaderInverse
                        eyebrow={stepCardEyebrow}
                        badge={stepCardBadge}
                        title={currentPrompt.label}
                        titleId={stepTitleId}
                        titleSize="item"
                    />
                    <div className="inspect-flow-wizard-card-body">
                        {currentPrompt.description && (
                            <div className="inspect-flow-wizard-what-to-check" id={stepQuestionId}>
                                {currentPrompt.description}
                            </div>
                        )}
                        <div
                            className="inspect-flow-wizard-response-grid inspect-flow-wizard-response-grid--binary"
                            role="radiogroup"
                            aria-labelledby={stepTitleId}
                            aria-describedby={currentPrompt.description ? stepQuestionId : undefined}
                            onKeyDown={handleNewIssuesRadioKeyDown}
                        >
                            {DAILY_SERVICE_NEW_ISSUES_CHOICES.map((choice) => {
                                const tone = resolveOperatorChoiceTone(choice.value);
                                const isSelected = currentPrompt.value === choice.value;
                                const showEmbeddedDetails =
                                    isSelected && isDailyServiceNewIssuesRecording(choice.value);

                                return (
                                    <ResponseOptionWrap
                                        key={choice.value}
                                        tone={tone}
                                        isSelected={isSelected}
                                        showEmbeddedDetails={showEmbeddedDetails}
                                    >
                                        <button
                                            type="button"
                                            role="radio"
                                            aria-checked={isSelected}
                                            aria-label={choice.label}
                                            aria-controls={
                                                showEmbeddedDetails
                                                    ? `inspect-flow-new-issues-details-${activeStepIndex}-embedded inspect-flow-new-issues-details-${activeStepIndex}-standalone`
                                                    : undefined
                                            }
                                            className={clsx(
                                                'inspect-flow-response-option',
                                                `tone-${tone}`,
                                                isSelected && 'is-selected',
                                            )}
                                            onClick={() => onPromptValueChange(activeStepIndex, choice.value)}
                                        >
                                            <span className="inspect-flow-response-heading">
                                                <span className="inspect-flow-response-icon" aria-hidden="true">
                                                    <InspectFlowResponseIcon tone={tone} />
                                                </span>
                                                <span className="inspect-flow-response-title">{choice.label}</span>
                                            </span>
                                        </button>
                                        {showEmbeddedDetails && renderNewIssuesDetails('embedded')}
                                    </ResponseOptionWrap>
                                );
                            })}
                        </div>
                        {isDailyServiceNewIssuesRecording(currentPrompt.value) &&
                            renderNewIssuesDetails('standalone')}
                    </div>
                    <WizardStepNav
                        primaryButtonLabel={primaryButtonLabel}
                        disableNext={disableNext}
                        nextBlockedReason={nextStepBlockedReason}
                        onNext={onNext}
                        onPrevious={onPrevious}
                        showPrevious={activeStepIndex > 0}
                    />
                </Card>
            )}

            {isAcknowledgement && currentPrompt && (
                <Card variant="card-lg" className="inspect-flow-wizard-card">
                    <CardHeaderInverse
                        eyebrow={stepCardEyebrow}
                        badge={stepCardBadge}
                        title={currentPrompt.label}
                        titleId={stepTitleId}
                        titleSize="item"
                    />
                    <div className="inspect-flow-wizard-card-body">
                        {currentPrompt.description && (
                            <div className="inspect-flow-wizard-what-to-check" id={stepQuestionId}>
                                {currentPrompt.description}
                            </div>
                        )}
                        <div
                            className="inspect-flow-checkbox-options"
                            role="group"
                            aria-labelledby={stepTitleId}
                            aria-describedby={currentPrompt.description ? stepQuestionId : undefined}
                        >
                            <button
                                type="button"
                                role="checkbox"
                                aria-checked={currentPrompt.value === OPERATOR_RESPONSE_VALUES.acknowledged}
                                className={clsx(
                                    'inspect-flow-checkbox-option',
                                    currentPrompt.value === OPERATOR_RESPONSE_VALUES.acknowledged && 'is-selected',
                                )}
                                onClick={() =>
                                    onPromptValueChange(
                                        activeStepIndex,
                                        currentPrompt.value === OPERATOR_RESPONSE_VALUES.acknowledged
                                            ? ''
                                            : OPERATOR_RESPONSE_VALUES.acknowledged,
                                    )
                                }
                            >
                                <span className="inspect-flow-checkbox-box" aria-hidden="true">
                                    {currentPrompt.value === OPERATOR_RESPONSE_VALUES.acknowledged && (
                                        <svg
                                            className="inspect-flow-checkbox-check"
                                            viewBox="0 0 12 10"
                                            fill="none"
                                        >
                                            <path
                                                d="M1 5.5L4.5 9L11 1.5"
                                                stroke="currentColor"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            />
                                        </svg>
                                    )}
                                </span>
                                <span className="inspect-flow-checkbox-label">Completed</span>
                            </button>
                        </div>
                    </div>
                    <WizardStepNav
                        primaryButtonLabel={primaryButtonLabel}
                        disableNext={disableNext}
                        nextBlockedReason={nextStepBlockedReason}
                        onNext={onNext}
                        onPrevious={onPrevious}
                        showPrevious={activeStepIndex > 0}
                    />
                </Card>
            )}

            {isStandAloneField && currentPrompt && (
                <Card variant="card-lg" className="inspect-flow-wizard-card">
                    <CardHeaderInverse
                        title={currentPrompt.label}
                        subtitle={currentPrompt.description}
                    />
                    <div className="inspect-flow-wizard-card-body">
                        {currentPrompt.inputMode === INPUT_MODES.TEXTAREA ? (
                            <textarea
                                id={`inspect-flow-standalone-${activeStepIndex}`}
                                className="inspect-flow-wizard-question-input inspect-flow-wizard-question-textarea"
                                value={currentPrompt.value}
                                onChange={(event) => onPromptValueChange(activeStepIndex, event.target.value)}
                                autoFocus
                            />
                        ) : (
                            <input
                                id={`inspect-flow-standalone-${activeStepIndex}`}
                                className="inspect-flow-wizard-question-input"
                                type={currentPrompt.inputMode === INPUT_MODES.NUMBER ? 'number' : 'text'}
                                inputMode={
                                    currentPrompt.inputMode === INPUT_MODES.NUMBER ? 'decimal' : 'text'
                                }
                                value={currentPrompt.value}
                                onChange={(event) => onPromptValueChange(activeStepIndex, event.target.value)}
                                autoFocus
                            />
                        )}
                    </div>
                    <WizardStepNav
                        primaryButtonLabel={primaryButtonLabel}
                        disableNext={disableNext}
                        nextBlockedReason={nextStepBlockedReason}
                        onNext={onNext}
                        onPrevious={onPrevious}
                        showPrevious={activeStepIndex > 0}
                    />
                </Card>
            )}

            {!isBeforeBegin &&
                !isFourResponse &&
                !isYesNo &&
                !isCheckbox &&
                !isDropdown &&
                !isSectionQuestion &&
                !isOperatorResponse &&
                !isQuantityOnly &&
                !isAcknowledgement &&
                !isNewIssues &&
                !isStandAloneField &&
                legacyPromptContent && (
                <Card variant="card-lg" className="inspect-flow-wizard-card inspect-flow-wizard-legacy card-pad-lg">
                    {legacyPromptContent}
                    <WizardStepNav
                        primaryButtonLabel={primaryButtonLabel}
                        disableNext={disableNext}
                        nextBlockedReason={nextStepBlockedReason}
                        onNext={onNext}
                        onPrevious={onPrevious}
                        showPrevious={activeStepIndex > 0}
                    />
                </Card>
            )}
        </div>
    );
};
